// @flow
import fp from "lodash/fp"
import { createSelector } from "reselect"
import * as R from "ramda"
import type { Dispatch } from "redux"
import { Map } from "immutable"
import type {
  Combination,
  PassengerGroup as BaggageGroup,
} from "@kiwicom/nitro/lib/records/Baggage"

import Price from "common/records/Price"
import { mapperBaggage } from "../../../services/mapper/services/mapperAvailableBaggage"
import { RELOAD_SUCCESS } from "../../../services/consts"
import {
  bidSelector,
  bookingPassengersSelector,
  currentBookingJourneySelector,
} from "../../../services/selector"
import { baggagesSelector } from "../../BookingBaggages/services/baggagesSelector"
import { getBaggage as loadBaggageApi } from "mmb/services/bookings/bookingsApi"
import mmbTrack from "mmb/services/tracking/mmbTrack"
import {
  BaggageModule,
  ErrorOccurredAction,
  GetDataAction,
} from "mmb/services/tracking/constants/trackingEvents"
import {
  CATEGORY_API_CONVERTER,
  CHANGE_PASSENGER_HAND_BAG,
  CHANGE_PASSENGER_HOLD_BAG,
  LOAD_BAGGAGE,
  LOAD_BAGGAGE_FAILURE,
  LOAD_BAGGAGE_SUCCESS,
  REINITIALIZE,
  SELECT_PASSENGER_BAGGAGE,
} from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingAddBags/services/constants"
import type {
  Baggage,
  State,
} from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingAddBags/services/types"

const DEFAULT_REINITIALIZE_STATE = {
  selectedPassenger: null,
}

const DEFAULT_STATE: State = {
  baggage: {
    definitions: {
      handBag: [],
      holdBag: [],
    },
    combinations: {
      handBag: [],
      holdBag: [],
    },
    original: {
      definitions: {
        hand_bag: [],
        hold_bag: [],
      },
      combinations: {
        hand_bag: [],
        hold_bag: [],
      },
      passenger_combinations: {
        hand_bag: {},
        hold_bag: {},
      },
    },
    passengerCombinations: {
      handBag: {},
      holdBag: {},
    },
    passengerSelectedCombinations: {
      handBag: {},
      holdBag: {},
    },
    ...DEFAULT_REINITIALIZE_STATE,
  },
  loading: false,
  error: null,
  success: null,
}

export const getGroupCombinations = (group: BaggageGroup) => (combinations: Combination[]) =>
  // $FlowFixMe
  combinations.filter(combination =>
    // $FlowFixMe
    R.pathOr([], ["conditions", "passengerGroups"], combination).includes(group),
  )

const bookingBaggage = (state: State = DEFAULT_STATE, action: any): State => {
  // TODO hack, find reason why is this immutable
  const newState = state instanceof Map ? DEFAULT_STATE : state

  switch (action.type) {
    case RELOAD_SUCCESS:
      return DEFAULT_STATE
    case REINITIALIZE:
      // $FlowFixMe
      return R.mergeDeepLeft(
        {
          baggage: {
            ...DEFAULT_REINITIALIZE_STATE,
            passengerSelectedCombinations: { ...newState.baggage.passengerCombinations },
          },
        },
        newState,
      )
    case LOAD_BAGGAGE_SUCCESS:
      return R.mergeLeft({ baggage: action.res, success: true }, newState)
    case SELECT_PASSENGER_BAGGAGE:
      return R.assocPath(["baggage", "selectedPassenger"], action.payload.passenger, newState)
    case CHANGE_PASSENGER_HOLD_BAG:
      return R.assocPath(
        ["baggage", "passengerSelectedCombinations", "holdBag", action.payload.selectedPassenger],
        action.payload.nextCombination,
        newState,
      )
    case CHANGE_PASSENGER_HAND_BAG:
      return R.assocPath(
        ["baggage", "passengerSelectedCombinations", "handBag", action.payload.selectedPassenger],
        action.payload.nextCombination,
        newState,
      )
    case LOAD_BAGGAGE_FAILURE:
      return R.mergeLeft({ success: false }, newState)
    default:
      return newState
  }
}

// eslint-disable-next-line flowtype/no-weak-types
export const loadBaggage = () => async (dispatch: Dispatch<*>, getState: () => Object) => {
  const state = getState()
  const bid = bidSelector(state)
  const journey = currentBookingJourneySelector(state)

  const ok = await dispatch({
    type: LOAD_BAGGAGE,
    // payload: { baggageData: mapperBaggage(res, journey) },
    promise: loadBaggageApi(bid).then(res => mapperBaggage(res, journey)),
    action: LOAD_BAGGAGE,
  })

  if (ok) {
    mmbTrack({ module: BaggageModule, action: GetDataAction, detail: "" })
  } else {
    mmbTrack({ module: BaggageModule, action: ErrorOccurredAction, detail: "API error" })
  }
}

export const reinitializeAction = () => ({
  type: REINITIALIZE,
})

export const selectPassengerBaggage = (passenger: number) => ({
  type: SELECT_PASSENGER_BAGGAGE,
  payload: { passenger },
  _track: { passenger },
})

export const changePassengerHoldBag = (nextCombination: number, selectedPassenger: number) => ({
  type: CHANGE_PASSENGER_HOLD_BAG,
  payload: { nextCombination, selectedPassenger },
})

export const changePassengerHandBag = (nextCombination: number, selectedPassenger: number) => ({
  type: CHANGE_PASSENGER_HAND_BAG,
  payload: { nextCombination, selectedPassenger },
})

// Data selectors
// ===
export const getBaggage = (state: any): Baggage => state.bookingBaggage.baggage

export const isLoadedSelector = (state: any): boolean => state.bookingBaggage.success

export default bookingBaggage

export const getCombinationsSelector: $FlowFixMe = createSelector(
  getBaggage,
  fp.get("combinations"),
)
export const getPassengerCombinationsSelector: $FlowFixMe = createSelector(
  getBaggage,
  fp.get("passengerCombinations"),
)

export const getHandBagCombinationsSelector: $FlowFixMe = createSelector(
  getCombinationsSelector,
  combinations => combinations.handBag,
)
export const getHoldBagCombinationsSelector: $FlowFixMe = createSelector(
  getCombinationsSelector,
  combinations => combinations.holdBag,
)
export const getAdultHandBagCombinationsSelector: $FlowFixMe = createSelector(
  getHandBagCombinationsSelector,
  getGroupCombinations("adult"),
)
export const getAdultHoldBagCombinationsSelector: $FlowFixMe = createSelector(
  getHoldBagCombinationsSelector,
  getGroupCombinations("adult"),
)

export const getDefinitionsSelector: $FlowFixMe = createSelector(
  getBaggage,
  fp.get("definitions"),
)
export const getSelectedPassengerSelector: $FlowFixMe = createSelector(
  getBaggage,
  fp.get("selectedPassenger"),
)

export const getHoldBagDefinitionsSelector: $FlowFixMe = createSelector(
  getDefinitionsSelector,
  fp.get("holdBag"),
)
export const getHandBagDefinitionsSelector: $FlowFixMe = createSelector(
  getDefinitionsSelector,
  fp.get("handBag"),
)

const formatRawDataToDefinitions = rawBaggage => {
  return rawBaggage.map(({ bag, price }) => {
    return {
      category: CATEGORY_API_CONVERTER[bag.category],
      price,
      restrictions: bag,
      conditions: { passengerGroups: ["adult", "child"] },
    }
  })
}

const handBagCombinationWithoutPriority = (combination, index, selected): any => {
  const priorityAirlines = combination.conditions.isPriority || []
  return priorityAirlines.length <= 0 || (priorityAirlines.length > 0 && index === selected)
}

export const paxBaggageSelector: (state: any, props: any) => any = createSelector(
  [baggagesSelector, getBaggage, bookingPassengersSelector],
  (rawBaggage, baggage, passengers) => {
    return Object.keys(baggage.passengerCombinations.handBag).reduce((acc, paxId) => {
      const handBag = baggage.passengerCombinations.handBag[paxId]
      const holdBag = baggage.passengerCombinations.holdBag[paxId]

      const pax = passengers.find(p => p.id === Number(paxId))

      const passenger = {
        paxId: pax.id,
        firstName: pax.firstname,
        middleName: pax.middlename,
        lastName: pax.lastname,
        title: pax.title,
        category: pax.category,
        dayOfBirth: pax.birth.format("L"),
      }

      // use raw data when there is no available combination
      if (handBag === -1 || holdBag === -1) {
        return [
          ...acc,
          {
            ...passenger,
            newDefinitions: formatRawDataToDefinitions(
              rawBaggage.filter(e => e.pid === Number(paxId)),
            ),
          },
        ]
      }

      const selected = {
        handBag: baggage.passengerSelectedCombinations.handBag[paxId],
        holdBag: baggage.passengerSelectedCombinations.holdBag[paxId],
      }

      // BE cant order baggage with priority so we need to filter it
      // but we also need to keep baggage with priority from booking
      const filteredHandCombination = baggage.combinations.handBag.filter((comb, i) =>
        handBagCombinationWithoutPriority(comb, i, handBag),
      )

      return [
        ...acc,
        {
          current: { handBag, holdBag },
          selected,
          baggage: R.assocPath(["combinations", "handBag"], filteredHandCombination, baggage),
          ...passenger,
        },
      ]
    }, [])
  },
)

// maybe we can use nitro helper for that
export const selectedBaggageTotalPriceSelector: (state: any, props: any) => any = createSelector(
  [getBaggage],
  ({ passengerCombinations, passengerSelectedCombinations, combinations }) => {
    const totalPriceHoldBag = Price.sum(
      Object.keys(passengerCombinations.holdBag)
        .filter(
          paxId =>
            passengerCombinations.holdBag[paxId] !== passengerSelectedCombinations.holdBag[paxId],
        )
        .map(paxId => {
          return new Price({
            amount:
              combinations.holdBag[passengerSelectedCombinations.holdBag[paxId]].price.amount -
              combinations.holdBag[passengerCombinations.holdBag[paxId]].price.amount,
            service:
              combinations.holdBag[passengerSelectedCombinations.holdBag[paxId]].price.service -
              combinations.holdBag[passengerCombinations.holdBag[paxId]].price.service,
          })
        }),
    )

    const totalPriceHandBag: Price = Price.sum(
      Object.keys(passengerCombinations.handBag)
        .filter(
          paxId =>
            passengerCombinations.handBag[paxId] !== passengerSelectedCombinations.handBag[paxId],
        )
        .map(paxId => {
          return new Price({
            amount:
              combinations.handBag[passengerSelectedCombinations.handBag[paxId]].price.amount -
              combinations.handBag[passengerCombinations.handBag[paxId]].price.amount,
            service:
              combinations.handBag[passengerSelectedCombinations.handBag[paxId]].price.service -
              combinations.handBag[passengerCombinations.handBag[paxId]].price.service,
          })
        }),
    )

    return Price.sum([totalPriceHoldBag, totalPriceHandBag])
  },
)

export const combinationsToPaySelector: (state: any, props: any) => any = createSelector(
  [getBaggage],
  baggage => {
    const holdBag = Object.keys(baggage.passengerCombinations.holdBag)
      .filter(
        paxId =>
          baggage.passengerCombinations.holdBag[paxId] !==
          baggage.passengerSelectedCombinations.holdBag[paxId],
      )
      .map(paxId => ({
        passenger_id: Number(paxId),
        combination: baggage.passengerSelectedCombinations.holdBag[paxId],
        category: "hold_bag",
      }))

    const handBag = Object.keys(baggage.passengerCombinations.handBag)
      .filter(
        paxId =>
          baggage.passengerCombinations.handBag[paxId] !==
          baggage.passengerSelectedCombinations.handBag[paxId],
      )
      .map(paxId => ({
        passenger_id: Number(paxId),
        combination: baggage.passengerSelectedCombinations.handBag[paxId],
        category: "hand_bag",
      }))

    return [...holdBag, ...handBag]
  },
)
